import { useState, useEffect } from 'react';
import { useAdminApi } from '../hooks/useAdminApi';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Save, X, History, AlertTriangle, MapPin, Clock, Package, Loader2, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Modal d'édition admin pour corriger un rapport chauffeur terminé.
 * - Édite date/heures/km/sous-traitant du rapport
 * - Édite chaque arrêt (client, adresse, heures, palettes, bons, notes, type)
 * - Affiche un onglet "Historique" listant toutes les modifications admin
 * - Régénère les factures brouillon automatiquement côté backend
 */
const AdminReportEditModal = ({ open, onClose, driverId, report, onSaved }) => {
  const { patch, get, post } = useAdminApi();
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('report'); // 'report' | 'stops' | 'history'
  const [reportDraft, setReportDraft] = useState({});
  const [stopsDraft, setStopsDraft] = useState([]);
  const [stopsOrderChanged, setStopsOrderChanged] = useState(false);
  const [history, setHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (!report) return;
    setReportDraft({
      date: report.date || '',
      end_date: report.end_date || '',
      start_time: report.start_time || '',
      end_time: report.end_time || '',
      total_km: report.total_km ?? '',
      subcontractor: report.subcontractor || '',
      notes: report.notes || '',
    });
    setStopsDraft((report.stops || []).map((s) => ({ ...s })));
    setStopsOrderChanged(false);
    setTab('report');
  }, [report]);

  const loadHistory = async () => {
    if (!driverId || !report?.id) return;
    setLoadingHistory(true);
    const res = await get(`/saas/driver/${driverId}/reports/${report.id}/admin-edits`);
    setLoadingHistory(false);
    if (res.success) setHistory(res.data);
  };

  useEffect(() => {
    if (open && tab === 'history') loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  const updateReportField = (key, value) => {
    setReportDraft((prev) => ({ ...prev, [key]: value }));
  };

  const updateStopField = (idx, key, value) => {
    setStopsDraft((prev) => prev.map((s, i) => (i === idx ? { ...s, [key]: value } : s)));
  };

  const moveStopUp = (idx) => {
    if (idx === 0) return;
    setStopsDraft((prev) => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
    setStopsOrderChanged(true);
  };

  const moveStopDown = (idx) => {
    setStopsDraft((prev) => {
      if (idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
      return next;
    });
    setStopsOrderChanged(true);
  };

  const sortStopsChronologically = () => {
    setStopsDraft((prev) => {
      const sorted = [...prev].sort((a, b) => {
        const timeA = a.arrival_iso || a.arrival_time || '';
        const timeB = b.arrival_iso || b.arrival_time || '';
        return timeA.localeCompare(timeB);
      });
      // Détecter si l'ordre a changé
      const changed = sorted.some((s, i) => s.id !== prev[i]?.id);
      if (changed) setStopsOrderChanged(true);
      return sorted;
    });
  };

  // Compute the changed fields on the report (only send what differs)
  const buildReportPayload = () => {
    const payload = {};
    for (const [k, v] of Object.entries(reportDraft)) {
      const orig = report[k];
      const normalizedNew = v === '' ? null : v;
      const normalizedOld = orig === undefined ? null : orig;
      if (normalizedNew !== null && normalizedNew !== normalizedOld) {
        payload[k] = normalizedNew;
      }
    }
    // Convert total_km to number if provided
    if ('total_km' in payload) {
      const n = Number(payload.total_km);
      payload.total_km = Number.isFinite(n) ? n : payload.total_km;
    }
    return payload;
  };

  const changedStops = () => {
    const changes = [];
    stopsDraft.forEach((current, idx) => {
      const original = (report.stops || [])[idx];
      if (!original) return;
      const diff = {};
      for (const key of Object.keys(current)) {
        if (key === 'documents' || key === 'id' || key === 'signature') continue;
        if (current[key] !== original[key]) diff[key] = current[key];
      }
      if (Object.keys(diff).length > 0) {
        changes.push({ id: current.id, diff });
      }
    });
    return changes;
  };

  const handleSave = async () => {
    if (!report?.id) return;
    setSaving(true);
    const errors = [];
    let successCount = 0;

    // 1. Réorganisation des arrêts (si l'ordre a changé)
    if (stopsOrderChanged && stopsDraft.length > 0) {
      const stop_ids = stopsDraft.map((s) => s.id);
      const res = await post(`/saas/driver/${driverId}/reports/${report.id}/stops/reorder`, { stop_ids });
      if (res.success) {
        successCount++;
      } else {
        errors.push(`Réorganisation: ${res.error || 'erreur inconnue'} (${res.status || 0})`);
      }
    }

    // 2. Champs du rapport
    const reportPayload = buildReportPayload();
    if (Object.keys(reportPayload).length > 0) {
      const res = await patch(`/saas/driver/${driverId}/reports/${report.id}`, reportPayload);
      if (res.success) {
        successCount++;
      } else {
        errors.push(`Rapport: ${res.error || 'erreur inconnue'} (${res.status || 0})`);
      }
    }

    // 3. Chaque stop modifié
    const stopChanges = changedStops();
    for (const c of stopChanges) {
      const res = await patch(`/saas/driver/${driverId}/reports/${report.id}/stops/${c.id}`, c.diff);
      if (res.success) {
        successCount++;
      } else {
        errors.push(`Arrêt ${c.id.slice(0, 8)}: ${res.error || 'erreur inconnue'} (${res.status || 0})`);
      }
    }

    setSaving(false);

    if (successCount === 0 && errors.length === 0) {
      toast.info('Aucun changement à enregistrer');
      return;
    }
    if (errors.length > 0) {
      console.error('[AdminReportEdit] Erreurs:', errors);
      errors.forEach((e) => toast.error(e, { duration: 8000 }));
      return;
    }
    toast.success(`Rapport corrigé — les factures brouillon liées ont été régénérées`);
    onSaved?.();
    onClose?.();
  };

  if (!report) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-4xl max-h-[90vh] overflow-hidden p-0">
        <DialogHeader className="p-4 border-b border-slate-800">
          <DialogTitle className="text-white text-lg flex items-center gap-2">
            <span className="text-amber-500">✏️</span>
            Éditer {report.report_number}
            {report.admin_edited && (
              <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded ml-2">
                Déjà modifié
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex border-b border-slate-800 bg-slate-900">
          <TabBtn active={tab === 'report'} onClick={() => setTab('report')} icon={Clock}>
            Rapport
          </TabBtn>
          <TabBtn active={tab === 'stops'} onClick={() => setTab('stops')} icon={MapPin}>
            Arrêts ({stopsDraft.length})
          </TabBtn>
          <TabBtn active={tab === 'history'} onClick={() => setTab('history')} icon={History}>
            Historique
          </TabBtn>
        </div>

        <div className="overflow-auto max-h-[calc(90vh-220px)] p-4">
          {tab === 'report' && (
            <div className="space-y-4">
              <WarningBanner />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Date" testid="edit-report-date">
                  <Input
                    type="date"
                    value={reportDraft.date}
                    onChange={(e) => updateReportField('date', e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="edit-report-date-input"
                  />
                </Field>
                <Field label="Date de fin (si multi-jour)" testid="edit-report-end-date">
                  <Input
                    type="date"
                    value={reportDraft.end_date}
                    onChange={(e) => updateReportField('end_date', e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="edit-report-end-date-input"
                  />
                </Field>
                <Field label="Heure de début" testid="edit-report-start-time">
                  <Input
                    type="time"
                    value={reportDraft.start_time}
                    onChange={(e) => updateReportField('start_time', e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="edit-report-start-time-input"
                  />
                </Field>
                <Field label="Heure de fin" testid="edit-report-end-time">
                  <Input
                    type="time"
                    value={reportDraft.end_time}
                    onChange={(e) => updateReportField('end_time', e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="edit-report-end-time-input"
                  />
                </Field>
                <Field label="Distance totale (km)" testid="edit-report-km">
                  <Input
                    type="number"
                    step="0.1"
                    value={reportDraft.total_km}
                    onChange={(e) => updateReportField('total_km', e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="edit-report-km-input"
                  />
                </Field>
                <Field label="Sous-traitant" testid="edit-report-subcontractor">
                  <Input
                    value={reportDraft.subcontractor}
                    onChange={(e) => updateReportField('subcontractor', e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="edit-report-subcontractor-input"
                  />
                </Field>
              </div>
              <Field label="Notes" testid="edit-report-notes">
                <Textarea
                  value={reportDraft.notes}
                  onChange={(e) => updateReportField('notes', e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white min-h-20"
                  placeholder="Notes internes (visibles admin uniquement)"
                  data-testid="edit-report-notes-input"
                />
              </Field>
            </div>
          )}

          {tab === 'stops' && (
            <div className="space-y-4">
              <WarningBanner />
              {stopsDraft.length >= 2 && (
                <div className="flex items-center justify-between bg-slate-800/60 rounded-lg p-3 border border-slate-700">
                  <div className="text-xs text-slate-300">
                    <span className="text-amber-400 font-semibold">Ordre actuel</span> : utilisez les flèches ou triez automatiquement par heure d'arrivée.
                  </div>
                  <Button
                    variant="outline"
                    onClick={sortStopsChronologically}
                    className="border-amber-600 text-amber-400 hover:bg-amber-900/30 h-8 text-xs"
                    data-testid="sort-stops-chrono-btn"
                  >
                    <ArrowUpDown size={12} className="mr-1" />
                    Trier chronologiquement
                  </Button>
                </div>
              )}
              {stopsOrderChanged && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-2 text-xs text-blue-300">
                  ⚠️ Ordre modifié — cliquez « Enregistrer » pour valider.
                </div>
              )}
              {stopsDraft.length === 0 && (
                <p className="text-slate-500 text-sm">Ce rapport n'a aucun arrêt.</p>
              )}
              {stopsDraft.map((stop, idx) => (
                <StopEditor
                  key={stop.id || idx}
                  index={idx}
                  totalStops={stopsDraft.length}
                  stop={stop}
                  onChange={(k, v) => updateStopField(idx, k, v)}
                  onMoveUp={() => moveStopUp(idx)}
                  onMoveDown={() => moveStopDown(idx)}
                />
              ))}
            </div>
          )}

          {tab === 'history' && (
            <HistoryPane loading={loadingHistory} history={history} />
          )}
        </div>

        <DialogFooter className="p-4 border-t border-slate-800 flex-row gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 border-slate-700"
            data-testid="edit-report-cancel-btn"
          >
            <X size={16} className="mr-2" />
            Annuler
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-amber-500 hover:bg-amber-600 text-black disabled:opacity-60"
            data-testid="edit-report-save-btn"
          >
            {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
            {saving ? 'Enregistrement…' : 'Enregistrer les corrections'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// Sub-components
// ============================================================

const TabBtn = ({ active, onClick, icon: Icon, children }) => (
  <button
    onClick={onClick}
    className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
      active
        ? 'text-amber-500 border-b-2 border-amber-500 bg-slate-800/40'
        : 'text-slate-400 hover:text-white'
    }`}
    data-testid={`edit-tab-${children.toString().toLowerCase().replace(/[^a-z]/g, '')}`}
  >
    <Icon size={14} />
    {children}
  </button>
);

const Field = ({ label, children, testid }) => (
  <div className="space-y-1" data-testid={testid}>
    <Label className="text-slate-400 text-xs uppercase tracking-widest">{label}</Label>
    {children}
  </div>
);

const WarningBanner = () => (
  <div className="bg-amber-500/10 border-l-4 border-amber-500 rounded-r-lg p-3 text-xs flex items-start gap-2">
    <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />
    <div className="text-amber-100/80">
      Toute modification est <strong>journalisée</strong> et régénère les factures brouillon liées.
      Le chauffeur ne verra <strong>pas</strong> de notification (correction silencieuse).
    </div>
  </div>
);

const StopEditor = ({ index, totalStops, stop, onChange, onMoveUp, onMoveDown }) => {
  const stopTypes = [
    { value: 'pickup', label: 'Ramassage' },
    { value: 'delivery', label: 'Livraison' },
    { value: 'pause', label: 'Pause' },
  ];

  return (
    <div className="bg-slate-800 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold text-sm">
          {index + 1}
        </div>
        <span className="text-white font-semibold flex-1">
          {stop.client_name || 'Arrêt sans nom'}
        </span>
        {totalStops > 1 && (
          <div className="flex gap-1">
            <button
              type="button"
              onClick={onMoveUp}
              disabled={index === 0}
              className="w-8 h-8 rounded-lg bg-slate-700 hover:bg-slate-600 disabled:opacity-30 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors"
              data-testid={`stop-${index}-move-up`}
              aria-label="Monter"
            >
              <ArrowUp size={14} />
            </button>
            <button
              type="button"
              onClick={onMoveDown}
              disabled={index === totalStops - 1}
              className="w-8 h-8 rounded-lg bg-slate-700 hover:bg-slate-600 disabled:opacity-30 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors"
              data-testid={`stop-${index}-move-down`}
              aria-label="Descendre"
            >
              <ArrowDown size={14} />
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Nom du client" testid={`edit-stop-${index}-name`}>
          <Input
            value={stop.client_name || ''}
            onChange={(e) => onChange('client_name', e.target.value)}
            className="bg-slate-900 border-slate-700 text-white"
            data-testid={`edit-stop-${index}-name-input`}
          />
        </Field>
        <Field label="Type" testid={`edit-stop-${index}-type`}>
          <Select value={stop.stop_type || 'delivery'} onValueChange={(v) => onChange('stop_type', v)}>
            <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700 text-white">
              {stopTypes.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <Field label="Adresse" testid={`edit-stop-${index}-address`}>
        <Input
          value={stop.client_address || ''}
          onChange={(e) => onChange('client_address', e.target.value)}
          className="bg-slate-900 border-slate-700 text-white"
          data-testid={`edit-stop-${index}-address-input`}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Arrivée (ISO ou heure)" testid={`edit-stop-${index}-arrival`}>
          <Input
            value={stop.arrival_iso || stop.arrival_time || ''}
            onChange={(e) => onChange(stop.arrival_iso ? 'arrival_iso' : 'arrival_time', e.target.value)}
            placeholder="2026-08-31T14:24:00Z ou 14:24"
            className="bg-slate-900 border-slate-700 text-white text-xs"
            data-testid={`edit-stop-${index}-arrival-input`}
          />
        </Field>
        <Field label="Départ (ISO ou heure)" testid={`edit-stop-${index}-departure`}>
          <Input
            value={stop.departure_iso || stop.departure_time || ''}
            onChange={(e) => onChange(stop.departure_iso ? 'departure_iso' : 'departure_time', e.target.value)}
            placeholder="2026-08-31T14:24:00Z ou 14:24"
            className="bg-slate-900 border-slate-700 text-white text-xs"
            data-testid={`edit-stop-${index}-departure-input`}
          />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Nombre de palettes" testid={`edit-stop-${index}-pallets`}>
          <Input
            type="number"
            value={stop.pallet_count ?? stop.pallets ?? ''}
            onChange={(e) => onChange(stop.pallet_count !== undefined ? 'pallet_count' : 'pallets', e.target.value ? Number(e.target.value) : 0)}
            className="bg-slate-900 border-slate-700 text-white"
            data-testid={`edit-stop-${index}-pallets-input`}
          />
        </Field>
        <Field label="Numéro de bons" testid={`edit-stop-${index}-bons`}>
          <Input
            value={stop.bons_number || ''}
            onChange={(e) => onChange('bons_number', e.target.value)}
            className="bg-slate-900 border-slate-700 text-white"
            data-testid={`edit-stop-${index}-bons-input`}
          />
        </Field>
      </div>

      <Field label="Notes de l'arrêt" testid={`edit-stop-${index}-notes`}>
        <Textarea
          value={stop.notes || ''}
          onChange={(e) => onChange('notes', e.target.value)}
          className="bg-slate-900 border-slate-700 text-white min-h-16"
          data-testid={`edit-stop-${index}-notes-input`}
        />
      </Field>
    </div>
  );
};

const HistoryPane = ({ loading, history }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="animate-spin text-amber-500" />
      </div>
    );
  }
  if (!history || !history.edits || history.edits.length === 0) {
    return (
      <p className="text-slate-500 text-sm text-center py-8">
        Aucune modification admin enregistrée pour ce rapport.
      </p>
    );
  }
  return (
    <div className="space-y-3">
      {history.edits.slice().reverse().map((edit, i) => (
        <div key={i} className="bg-slate-800 rounded-lg p-3 border-l-4 border-blue-500">
          <div className="flex items-center justify-between mb-2 text-xs">
            <span className="text-white font-semibold">{edit.admin_name || edit.admin_email}</span>
            <span className="text-slate-400">{new Date(edit.timestamp).toLocaleString('fr-CA')}</span>
          </div>
          <div className="text-xs text-slate-400 mb-2">Cible : {edit.target}</div>
          <div className="space-y-1">
            {Object.entries(edit.changes || {}).map(([field, { old, new: nv }]) => (
              <div key={field} className="text-xs bg-slate-900 rounded p-2">
                <span className="text-amber-400 font-semibold">{field}</span> :{' '}
                <span className="text-red-400 line-through">{String(old ?? '—')}</span>{' '}
                → <span className="text-green-400">{String(nv ?? '—')}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AdminReportEditModal;
